import { TrialGranted, TrialGrantedEventPayload } from '@lifeway/frontend-analytics';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ErrorMessage from '../../../components/ErrorMessage';
import LoadingState from '../../../components/LoadingState';
import { createCurriculum } from '../../../hooks/useCurriculum';
import alertService from '../../../services/AlertService';
import userService from '../../../services/userService';
import { generateUUID } from '../../../services/uuid';
import windowService from '../../../services/windowService';
import { handleError } from '../../../utils/apiUtils';
import { logger } from '../../../utils/logger';
import { useAnalytics } from '../../analytics';
import { useLogin } from '../../authentication/hooks/useLogin';
import { BrandCode } from '../../curriculum';
import { createOrgAccount } from '../../org';
import { setRecentlyPaired } from '../../pairing';
import { Church, Datastore, Item } from '../models/datastore';
import { OnboardingType, OnboardingTypeConfig } from '../models/onboarding';
import { deleteDatastore, getStoredDatastore, updateDatastore } from '../utils/onboardingDatastore';
import { AgeGroupsView } from './AgeGroupsView';
import { ChurchView } from './ChurchView';
import { EmailView } from './EmailView';

type OnboardingProps = {
  brandCode: BrandCode;
  type: OnboardingType;
  getCurriculum: OnboardingTypeConfig['getCurriculum'];
  grantLicenses: OnboardingTypeConfig['grantLicenses'];
  alreadyGrantedComponent: ReturnType<OnboardingTypeConfig['alreadyGrantedComponent']>;
  userId?: string;
  userEmail?: string;
  preselectedAges?: string[];
};

export const OnboardingContent = ({
  userId,
  userEmail,
  brandCode,
  type,
  getCurriculum,
  grantLicenses,
  alreadyGrantedComponent,
  preselectedAges,
}: OnboardingProps) => {
  const [datastore, setDatastore] = useState(getStoredDatastore(brandCode));
  const [isAlreadyGranted, setIsAlreadyGranted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { redirectToLogin, redirectToRegister } = useLogin(false);

  const analytics = useAnalytics();
  const stepParam = parseInt(useParams<{ step?: string }>().step ?? '1', 10);
  const step = [1, 2, 3, 4].includes(stepParam) ? stepParam : 1;

  const updateDatastoreState = (data: Partial<Datastore>) => setDatastore(updateDatastore(brandCode, datastore, data));

  const getStepUrl = (step: number) => `/curriculum/onboarding/${brandCode}/${step}`;
  const goToStep = (step: number) => windowService.redirectTo(getStepUrl(step));

  const onTrialGranted = (items: Item[]) => {
    deleteDatastore(brandCode);

    const curriculum = createCurriculum(brandCode);
    if (curriculum) {
      setRecentlyPaired(items.map(i => ({ name: curriculum.brand.name, subtitle: i.name, brandCode })));
    }

    items.forEach(({ itemNumber, name: ageCategory }) => {
      if (!userEmail) return;
      const payload: TrialGrantedEventPayload = itemNumber
        ? { licenseId: undefined, itemNumber, userEmail }
        : { licenseId: undefined, trialCode: brandCode, userEmail };

      analytics.emit(new TrialGranted(payload, { curriculum: { brandCode, ageCategory } }));
    });

    alertService.showOnNextPage(`${type === 'preview' ? 'Preview' : 'Trial'} Granted`);
    windowService.redirectTo('/bible-studies');
  };

  const handleGrantLicenses = (items: Item[], userId: string, orgId: string, zipCode: string) => {
    grantLicenses(items, userId, orgId, zipCode, brandCode)
      .then(() => onTrialGranted(items))
      .catch(error => {
        if (error.reason === 'AlreadyGranted') {
          return setIsAlreadyGranted(true);
        }

        logger.error('Error granting licenses:', error);
        setHasError(true);
      });
  };

  const handleAgeGroups = (items: Item[]) => {
    updateDatastoreState({ items });
    goToStep(userId ? 3 : 2);
  };

  const handleEmail = (email: string) => {
    userService
      .getUserIdByEmail(email)
      .then(() => redirectToLogin(getStepUrl(3), email))
      .catch(() => {
        updateDatastoreState({ email });
        goToStep(3);
      });
  };

  const handleChurch = (church: Church) => {
    updateDatastoreState({ church });
    goToStep(4);
  };

  const completeOnboarding = () => {
    if (!datastore.items?.length || !datastore.church) {
      return setHasError(true);
    }
    const { items, church } = datastore;

    if (!userId) {
      if (!datastore.email) return setHasError(true);
      return redirectToRegister(getStepUrl(4));
    }

    if (!church.id) {
      if (!church.name || !church.zip) return setHasError(true);

      return generateUUID((id: string) =>
        createOrgAccount(id, church.name, userId)
          .then(() => {
            handleGrantLicenses(items, userId, id, church.zip);
          })
          .catch(handleError)
      );
    }

    handleGrantLicenses(items, userId, church.id, church.zip);
  };

  useEffect(() => {
    if (step === 4) {
      completeOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (hasError) {
    return <ErrorMessage />;
  }

  if (isAlreadyGranted) {
    return alreadyGrantedComponent;
  }

  switch (step) {
    case 1:
      return (
        <AgeGroupsView
          brandCode={brandCode}
          storedItems={datastore.items}
          preselectedAges={preselectedAges}
          getCurriculum={getCurriculum}
          handler={handleAgeGroups}
        />
      );
    case 2:
      return <EmailView storedEmail={datastore.email} handler={handleEmail} />;
    case 3:
      return <ChurchView type={type} handler={handleChurch} />;
    case 4: {
      return (
        <LoadingState>
          <h4>Finalizing onboarding process</h4>
        </LoadingState>
      );
    }
  }
};
